import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TicketPercent } from "lucide-react";
import { PaymentPlanResource } from "@/interfaces";
import { formatCurrency, formatDate } from "@/utils/currency";
import { planEligiblePromotions } from "@/utils/promotions";

interface Props {
  paymentPlans?: PaymentPlanResource[] | null;
}

type AvailableDiscount = {
  id: string;
  name?: string;
  amount?: number | null;
  percent?: number | null;
  stopAt?: string | null;
  installments: number[];
};

/**
 * One promotion is attached once per installment count, so the same discount
 * comes back on several plans. Group by promotion and collect the installment
 * counts it applies to.
 */
const groupByPromotion = (
  paymentPlans?: PaymentPlanResource[] | null
): AvailableDiscount[] => {
  const byPromotionId = new Map<string, AvailableDiscount>();

  for (const plan of paymentPlans ?? []) {
    const promotion = planEligiblePromotions(plan)[0];
    if (!promotion) continue;

    const id = String(promotion.id);
    const entry: AvailableDiscount = byPromotionId.get(id) ?? {
      id,
      name: promotion.attributes?.name,
      amount: promotion.attributes?.amount,
      percent: promotion.attributes?.percent,
      stopAt: promotion.attributes?.stop_at,
      installments: [],
    };

    if (!entry.installments.includes(plan.attributes.installments)) {
      entry.installments.push(plan.attributes.installments);
    }
    byPromotionId.set(id, entry);
  }

  return Array.from(byPromotionId.values()).map((discount) => ({
    ...discount,
    installments: [...discount.installments].sort((a, b) => a - b),
  }));
};

/**
 * Discounts the customer can still obtain, shown before the contract is signed
 * so the price they agree to is not contradicted by a cheaper one at the
 * payment step.
 */
const ContractAvailableDiscounts: FC<Props> = ({ paymentPlans }) => {
  const { t, i18n } = useTranslation();

  const discounts = useMemo(() => groupByPromotion(paymentPlans), [paymentPlans]);

  if (discounts.length === 0) {
    return null;
  }

  const discountValue = (discount: AvailableDiscount) => {
    if (discount.amount) {
      return formatCurrency(Math.abs(discount.amount), i18n.language);
    }
    if (discount.percent) {
      return `${discount.percent}%`;
    }
    return null;
  };

  const installmentsLabel = (installments: number[]) =>
    installments.length === 1 && installments[0] === 1
      ? t("fe.pages.flow.steps.contract.promotions-installment", {
          installments: installments[0],
          defaultValue: "with {{installments}} payment",
        })
      : t("fe.pages.flow.steps.contract.promotions-installments", {
          installments: installments.join(", "),
          defaultValue: "with {{installments}} payments",
        });

  return (
    <div className="mx-7 mt-6 rounded-2xl border border-[#0A4DBD]/20 bg-[#0A4DBD]/5 p-5 flex flex-col gap-3">
      <div className="flex items-center gap-2 text-[#0A4DBD]">
        <TicketPercent size={18} />
        <h3 className="text-sm leading-5 font-semibold">
          {t("fe.pages.flow.steps.contract.promotions-title", {
            defaultValue: "Discounts available",
          })}
        </h3>
      </div>

      <p className="text-sm leading-5 text-muted-foreground">
        {t("fe.pages.flow.steps.contract.promotions-subtitle", {
          defaultValue:
            "These discounts can be applied to your contract depending on the payment option you choose at the Payment Agreement step.",
        })}
      </p>

      <div className="flex flex-col gap-3">
        {discounts.map((discount) => {
          const value = discountValue(discount);

          return (
            <div
              key={discount.id}
              className="flex flex-wrap items-baseline justify-between gap-2 text-sm leading-5 text-card-foreground"
            >
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">{discount.name ?? "-"}</span>
                {discount.installments.length > 0 && (
                  <span className="text-xs leading-4 text-muted-foreground">
                    {installmentsLabel(discount.installments)}
                  </span>
                )}
                {discount.stopAt && (
                  <span className="text-xs leading-4 text-muted-foreground">
                    {t("fe.pages.flow.steps.contract.promotions-valid-until", {
                      date: formatDate(discount.stopAt, i18n.language),
                      defaultValue: "Valid only if paid before {{date}}",
                    })}
                  </span>
                )}
              </div>
              {value && (
                <span className="font-semibold text-[#0A4DBD]">-{value}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContractAvailableDiscounts;
